import React, { useState, useEffect } from 'react';
import {
    Card, 
    Row, 
    Col, 
    Tabs, 
    Table, 
    Tag, 
    Descriptions, 
    Typography, 
    Button, 
    Space,
    DatePicker,
    ConfigProvider,
    message,
    Input,
    Select,
    Result
} from 'antd';
import { ArrowLeftOutlined, CopyOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import zhCN from 'antd/lib/locale/zh_CN';
import FirmwareOtaTab from './FirmwareOtaTab';
import DeviceConfigTab from './DeviceConfigTab';
import DevicePackageTab from './DevicePackageTab';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const DeviceDetail = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState('info');

    // 设备查询栏状态
    const [queryType, setQueryType] = useState('license');
    const [queryKeyword, setQueryKeyword] = useState('');

    // 从 URL 参数回显查询内容
    useEffect(() => {
        const license = searchParams.get('license');
        const did = searchParams.get('did');
        if (license) { setQueryType('license'); setQueryKeyword(license); }
        else if (did) { setQueryType('did'); setQueryKeyword(did); }
    }, [searchParams]);

    const handleQuery = () => {
        if (!queryKeyword.trim()) return;
        const param = queryType === 'license' ? `license=${queryKeyword}` : `did=${queryKeyword}`;
        navigate(`/device/info?${param}`, { replace: true });
    };

    // 判断当前查询是否命中 mock 数据（test1）
    const queryLicense = searchParams.get('license');
    const queryDid = searchParams.get('did');
    const hasQuery = !!(queryLicense || queryDid);
    const isMatch = !(queryLicense === 'nodata' || queryDid === 'nodata');

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        message.success('已复制到剪贴板');
    };

    // 用户添加记录数据
    const userAddColumns = [
        { title: '序号', dataIndex: 'id', key: 'id', width: 70 },
        { title: '操作类型', dataIndex: 'type', key: 'type' },
        { title: '用户邮箱', dataIndex: 'email', key: 'email' },
        { title: '用户手机号', dataIndex: 'phone', key: 'phone' },
        { title: '发生时间', dataIndex: 'time', key: 'time', sorter: true },
        { title: '应用App', dataIndex: 'app', key: 'app' },
    ];

    const userAddData = [
        { id: 1, type: '用户绑定', email: '', phone: '13969183581', time: '2026-02-06 16:00:53', app: '' },
        { id: 2, type: '用户解绑', email: '', phone: '', time: '2026-02-06 15:56:16', app: '' },
    ];

    // 设备活跃记录数据
    const activeColumns = [
        { title: '序号', dataIndex: 'id', key: 'id', width: 70 },
        { title: '活动状态', dataIndex: 'status', key: 'status' },
        { title: '上下线时间', dataIndex: 'time', key: 'time' },
    ];

    // App录像设置 mock 数据
    const appRecordEnabled = true;
    const appRecordOnPeriod = { start: '08:00', end: '18:00' };
    const appRecordOffPeriod = { start: '18:00', end: '08:00' };
    const hasAppRecordTimeConfig = true;

    return (
        <ConfigProvider locale={zhCN}>
            <div style={{ background: '#f0f2f5', minHeight: '100vh', paddingBottom: '32px' }}>
                <div style={{ 
                    background: '#fff', 
                    padding: '24px 24px 48px', 
                    color: '#333',
                    borderBottom: '1px solid #e8e8e8'
                }}>
                    <Space size="large" style={{ width: '100%' }}>
                        <Button 
                            icon={<ArrowLeftOutlined />} 
                            onClick={() => navigate('/device/all')} 
                            style={{ 
                                background: '#f5f5f5', 
                                border: '1px solid #d9d9d9', 
                                color: '#666'
                            }}
                        >
                            返回列表
                        </Button>
                        <div style={{ flex: 1 }}>
                            <Title level={4} style={{ margin: 0, color: '#333' }}>设备详情</Title>
                        </div>
                        <Space size={8}>
                            <Select value={queryType} onChange={setQueryType} style={{ width: 110 }}>
                                <Select.Option value="license">License</Select.Option>
                                <Select.Option value="did">设备ID</Select.Option>
                            </Select>
                            <Input
                                placeholder={queryType === 'license' ? '请输入 License' : '请输入设备ID'}
                                value={queryKeyword}
                                onChange={(e) => setQueryKeyword(e.target.value)}
                                style={{ width: 240 }}
                                allowClear
                                onPressEnter={handleQuery}
                            />
                            <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery}>查询</Button>
                            <Text type="secondary" style={{ fontSize: 11 }}>输入 'nodata' 查看无设备交互</Text>
                        </Space>
                    </Space>
                </div>

                {/* 未查询到设备 */}
                {hasQuery && !isMatch && (
                    <div style={{ padding: '80px 24px', textAlign: 'center' }}>
                        <Result
                            status="404"
                            title="未找到该设备"
                            subTitle={
                                <span>
                                    未查询到 {queryLicense ? `License: ${queryLicense}` : `设备ID: ${queryDid}`} 的设备信息<br />
                                    <Text type="secondary" style={{ fontSize: 12 }}>输入 nodata 可查看未找到设备交互，输入其他值展示设备信息</Text>
                                </span>
                            }
                        />
                    </div>
                )}

                {/* 设备详情内容：无查询参数 或 命中 test1 */}
                {(!hasQuery || isMatch) && (
                <div style={{ padding: '24px' }}>
                    <Card bordered={false} bodyStyle={{ padding: '0 24px' }} style={{ borderRadius: '8px', marginBottom: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                        <Tabs 
                            activeKey={activeTab} 
                            onChange={setActiveTab}
                            tabBarStyle={{ marginBottom: 0, borderBottom: 'none' }}
                            items={[
                                { label: '设备信息', key: 'info' },
                                { label: '设备配置', key: 'config' },
                                { label: '设备套餐', key: 'package' },
                                { label: '操作记录', key: 'record' },
                                { label: '固件OTA', key: 'ota' }
                            ]}
                        />
                    </Card>

                    {activeTab === 'info' && (
                        <Row gutter={20}>
                            <Col span={10}>
                                <Card 
                                    title={<div style={{ borderLeft: '4px solid #1890ff', paddingLeft: '12px', fontWeight: 600 }}>基础信息</div>} 
                                    bordered={false} 
                                    style={{ marginBottom: 20, borderRadius: '8px' }}
                                >
                                    <Descriptions column={1} size="middle" labelStyle={{ color: '#8c8c8c' }}>
                                        <Descriptions.Item label="License">
                                            <Space>
                                                <Text strong>JpxTTR260206fGYa</Text>
                                                <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => handleCopy('JpxTTR260206fGYa')}>复制</Button>
                                            </Space>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="设备ID">
                                            <Space>
                                                <Text style={{ fontFamily: 'monospace' }}>DID-2026-JPX001</Text>
                                                <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => handleCopy('DID-2026-JPX001')}>复制</Button>
                                            </Space>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="产品信息">
                                            <Space>
                                                <Text>普通单目云台摄像机</Text>
                                                <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => handleCopy('普通单目云台摄像机')}>复制</Button>
                                            </Space>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="产品ID">P5xJS90pll</Descriptions.Item>
                                        <Descriptions.Item label="联网方式"><Tag color="processing">Wi-Fi</Tag></Descriptions.Item>
                                        <Descriptions.Item label="生产工单">手动烧录测试</Descriptions.Item>
                                        <Descriptions.Item label="企业名称">东莞康林物联网有限公司</Descriptions.Item>
                                        <Descriptions.Item label="企业ID">JGSf0Nd7</Descriptions.Item>
                                    </Descriptions>
                                </Card>
                                <Card 
                                    title={<div style={{ borderLeft: '4px solid #eb2f96', paddingLeft: '12px', fontWeight: 600 }}>绑定SaaS节点</div>} 
                                    bordered={false} 
                                    style={{ marginBottom: 20, borderRadius: '8px' }}
                                >
                                    <Table
                                        size="small"
                                        pagination={false}
                                        dataSource={[
                                            { key: '1', saasId: 'G001', saasName: '公版SaaS', bindTime: '2026-02-06 15:46:30' },
                                            { key: '2', saasId: 'G002', saasName: '云视SaaS', bindTime: '2026-03-10 09:20:15' },
                                        ]}
                                        columns={[
                                            { title: 'SaaSID', dataIndex: 'saasId', width: 120, render: (v, r) => v === 'G001' ? <Text>{r.saasName}</Text> : (<div><div style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</div><Text type="secondary" style={{ fontSize: 11 }}>{r.saasName}</Text></div>) },
                                            { title: '绑定时间', dataIndex: 'bindTime', width: 180 },
                                        ]}
                                    />
                                </Card>
                                <Card 
                                    title={<div style={{ borderLeft: '4px solid #722ed1', paddingLeft: '12px', fontWeight: 600 }}>归属用户信息</div>} 
                                    bordered={false} 
                                    style={{ borderRadius: '8px' }}
                                >
                                    <Descriptions column={2} size="middle">
                                        <Descriptions.Item label="登录账号"><Text strong>139****3581</Text></Descriptions.Item>
                                        <Descriptions.Item label="注册地区">中国 · 江苏</Descriptions.Item>
                                    </Descriptions>
                                </Card>
                            </Col>
                            <Col span={14}>
                                <Card 
                                    title={<div style={{ borderLeft: '4px solid #52c41a', paddingLeft: '12px', fontWeight: 600 }}>当前状态</div>} 
                                    bordered={false}
                                    style={{ borderRadius: '8px' }}
                                >
                                    <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fafafa', borderRadius: 8 }}>
                                        <Text strong style={{ fontSize: 13, color: '#1890ff' }}>设备状态</Text>
                                        <Descriptions column={2} size="small" labelStyle={{ color: '#8c8c8c' }} style={{ marginTop: 8 }}>
                                            <Descriptions.Item label="在线状态"><Space><Badge status="error" />离线</Space></Descriptions.Item>
                                            <Descriptions.Item label="TF卡">未插入</Descriptions.Item>

                                            <Descriptions.Item label="当前电量">82%</Descriptions.Item>
                                            <Descriptions.Item label="最后充电时间">2026-02-06 14:30:00</Descriptions.Item>
                                        </Descriptions>
                                    </div>
                                    <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fafafa', borderRadius: 8 }}>
                                        <Text strong style={{ fontSize: 13, color: '#722ed1' }}>App录像设置</Text>
                                        <Descriptions column={2} size="small" labelStyle={{ color: '#8c8c8c' }} style={{ marginTop: 8 }}>
                                            <Descriptions.Item label="录像状态">
                                                <Tag color={appRecordEnabled ? 'success' : 'default'} style={{ border: 'none' }}>
                                                    {appRecordEnabled ? '开启' : '关闭'}
                                                </Tag>
                                            </Descriptions.Item>
                                            {hasAppRecordTimeConfig && (
                                                <>
                                                    <Descriptions.Item label="开启时段">
                                                        {appRecordOnPeriod.start} ~ {appRecordOnPeriod.end}
                                                    </Descriptions.Item>
                                                    <Descriptions.Item label="关闭时段">
                                                        {appRecordOffPeriod.start} ~ {appRecordOffPeriod.end}
                                                    </Descriptions.Item>
                                                </>
                                            )}
                                        </Descriptions>
                                    </div>
                                    <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fafafa', borderRadius: 8 }}>
                                        <Text strong style={{ fontSize: 13, color: '#52c41a' }}>激活与绑定 —— 公版SaaS</Text>
                                        <Descriptions column={2} size="small" labelStyle={{ color: '#8c8c8c' }} style={{ marginTop: 8 }}>
                                            <Descriptions.Item label="激活状态"><Tag color="green" style={{ border: 'none' }}>已激活</Tag></Descriptions.Item>
                                            <Descriptions.Item label="激活时间">2026-02-06 15:10:15</Descriptions.Item>
                                            <Descriptions.Item label="绑定状态"><Tag color="blue" style={{ border: 'none' }}>已绑定</Tag></Descriptions.Item>
                                            <Descriptions.Item label="首次绑定">2026-02-06 15:46:26</Descriptions.Item>
                                            <Descriptions.Item label="最后绑定">2026-02-06 16:00:00</Descriptions.Item>
                                        </Descriptions>
                                    </div>
                                    <div style={{ padding: '12px 16px', background: '#fafafa', borderRadius: 8 }}>
                                        <Text strong style={{ fontSize: 13, color: '#fa8c16' }}>时间信息</Text>
                                        <Descriptions column={2} size="small" labelStyle={{ color: '#8c8c8c' }} style={{ marginTop: 8 }}>
                                            <Descriptions.Item label="创建时间">2026-01-15 10:00:00</Descriptions.Item>
                                            <Descriptions.Item label="生产时间">2026-01-20 08:15:30</Descriptions.Item>
                                            <Descriptions.Item label="最后上线">2026-02-06 15:57:58</Descriptions.Item>
                                            <Descriptions.Item label="最后离线">2026-02-06 18:19:33</Descriptions.Item>
                                        </Descriptions>
                                    </div>
                                    <div style={{ marginTop: 16, padding: '12px 16px', background: '#e6f7ff', borderRadius: 8, textAlign: 'center' }}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>当前固件版本</Text>
                                        <div style={{ fontSize: 20, fontWeight: 'bold', color: '#1890ff', margin: '4px 0' }}>26.01.09.2</div>
                                    </div>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {activeTab === 'config' && (
                        <DeviceConfigTab />
                    )}

                    {activeTab === 'package' && (
                        <DevicePackageTab />
                    )}

                    {activeTab === 'record' && (
                        <div>
                            <Card title="用户操作记录" bordered={false} style={{ marginBottom: 20, borderRadius: '8px' }}>
                                <Table 
                                    columns={userAddColumns} 
                                    dataSource={userAddData} 
                                    pagination={{
                                        showTotal: (total) => `共 ${total} 条记录`,
                                    }}
                                />
                            </Card>

                            <Card 
                                title="设备活跃记录"
                                bordered={false}
                                style={{ borderRadius: '8px' }}
                            >
                                <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <span style={{ fontSize: '14px', color: '#666' }}>上下线时间:</span>
                                    <RangePicker placeholder={['开始日期', '结束日期']} />
                                    <Button type="primary">查询</Button>
                                    <Button>重置</Button>
                                </div>
                                <Table 
                                    columns={activeColumns} 
                                    dataSource={[]} 
                                    locale={{ emptyText: '暂无在线记录' }}
                                    pagination={{
                                        showTotal: (total) => `共 ${total} 条记录`,
                                    }}
                                />
                            </Card>
                        </div>
                    )}

                    {activeTab === 'ota' && (
                        <FirmwareOtaTab
                            deviceLicense="JpxTTR260206fGYa"
                            currentFwVersion="26.01.09.2"
                            productName="普通单目云台摄像机"
                            productId="P5xJS90pll"
                            isPlatform={false}
                        />
                    )}
                </div>
                )}
            </div>
        </ConfigProvider>
    );
};

const Badge = ({ status }) => {
    const color = status === 'error' ? '#ff4d4f' : '#52c41a';
    return <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: color, marginRight: 8 }} />;
};

export default DeviceDetail;
